/**
 * VisionBoardPage — the "wall of A4 whiteboard slates" view at /board.
 *
 * Owner-facing, plain-English. Shows 10 app slates (CP, NB, EI, OC, EC, RI, MP,
 * CC, Test Call, SIP Server) each with a single "What to do next" list
 * (fix64 — consolidated from the previous waiting_on / next_steps split).
 *
 * Design ref: /app/design_guidelines.md §2 layout + §4 drill-down + §5 settings.
 *
 * fix64 — Owner-requested refinements
 *   a) Single "What to do next" lane (≤3 items)
 *   b) Cluster highlight: hovering any slate in a cluster dims non-cluster slates
 *      (EC ↔ CC ↔ Test Call ↔ SIP Server, semantic cluster_id `emergency_comms`)
 *   c) Drag-to-reorder slates in Edit mode (HTML5 native drag; persisted via
 *      /api/board/config slate_order)
 *   d) Stuck-status visual treatment (backend already returns status_tier="stuck"
 *      plus stuck_reason; drill-down surfaces the reason verbatim)
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { Pencil, Check, ArrowLeft, ExternalLink, Wand2, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { SlateCard } from "../components/SlateCard";
import { fetchSlates, fetchBoardConfig, saveBoardConfig } from "../lib/boardApi";

export default function VisionBoardPage() {
  const [slates, setSlates] = useState([]);
  const [config, setConfig] = useState({
    slate_colors: {},
    status_overrides: {},
    slate_order: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [openSlate, setOpenSlate] = useState(null);

  // Cluster hover state — when set, slates outside this cluster dim.
  // Edit-mode disables dimming so drag operations aren't visually distracting.
  const [hoveredClusterId, setHoveredClusterId] = useState(null);

  // Drag-to-reorder state (Edit mode only).
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [slatesRes, configRes] = await Promise.all([fetchSlates(), fetchBoardConfig()]);
      setSlates(slatesRes);
      setConfig({
        slate_colors: configRes.slate_colors || {},
        status_overrides: configRes.status_overrides || {},
        slate_order: configRes.slate_order || [],
      });
    } catch (err) {
      setError(err.message || "Failed to load the vision board");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const persistConfig = async (nextConfig, successMsg) => {
    try {
      await saveBoardConfig(nextConfig);
      if (successMsg) toast.success(successMsg, { duration: 1800 });
    } catch (err) {
      toast.error("Could not save change", { description: err.message });
    }
  };

  const handleColorChange = async (slateId, color) => {
    const newConfig = {
      ...config,
      slate_colors: { ...config.slate_colors, [slateId]: color },
    };
    setConfig(newConfig);
    setSlates((prev) =>
      prev.map((s) => (s.id === slateId ? { ...s, identity_color: color } : s))
    );
    persistConfig(newConfig, "Colour saved");
  };

  const handleStatusChange = async (slateId, status) => {
    const newConfig = {
      ...config,
      status_overrides: { ...config.status_overrides, [slateId]: status },
    };
    setConfig(newConfig);
    setSlates((prev) => prev.map((s) => (s.id === slateId ? { ...s, status_tier: status } : s)));
    persistConfig(newConfig, "Status set");
  };

  const handleStatusReset = async (slateId) => {
    const { [slateId]: _removed, ...rest } = config.status_overrides;
    const newConfig = { ...config, status_overrides: rest };
    setConfig(newConfig);
    try {
      await saveBoardConfig(newConfig);
      await loadAll();
      toast.success("Status reset to auto", { duration: 1800 });
    } catch (err) {
      toast.error("Could not reset status", { description: err.message });
    }
  };

  const isStatusOverridden = (slateId) =>
    Object.prototype.hasOwnProperty.call(config.status_overrides || {}, slateId);

  // ─────────────────────────────────────────────────────────────
  // Cluster highlight handlers
  // ─────────────────────────────────────────────────────────────
  const onClusterEnter = (clusterId) => {
    if (!editMode) setHoveredClusterId(clusterId);
  };
  const onClusterLeave = (clusterId) => {
    setHoveredClusterId((prev) => (prev === clusterId ? null : prev));
  };

  const isDimmed = (slate) => {
    if (editMode) return false;
    if (!hoveredClusterId) return false;
    return slate.cluster_id !== hoveredClusterId;
  };

  // ─────────────────────────────────────────────────────────────
  // Drag-to-reorder handlers (HTML5 native)
  // ─────────────────────────────────────────────────────────────
  const handleDragStart = (e, slateId) => {
    setDraggingId(slateId);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", slateId);
    } catch {
      // some browsers block setData on certain MIME types — ignore
    }
  };

  const handleDragOver = (e, slateId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (slateId !== dropTargetId) setDropTargetId(slateId);
  };

  const handleDragLeave = (_e, slateId) => {
    setDropTargetId((prev) => (prev === slateId ? null : prev));
  };

  const handleDrop = async (e, targetSlateId) => {
    e.preventDefault();
    const sourceId = draggingId || e.dataTransfer.getData("text/plain");
    setDraggingId(null);
    setDropTargetId(null);
    if (!sourceId || sourceId === targetSlateId) return;

    const currentIds = slates.map((s) => s.id);
    const sourceIdx = currentIds.indexOf(sourceId);
    const targetIdx = currentIds.indexOf(targetSlateId);
    if (sourceIdx === -1 || targetIdx === -1) return;

    const reordered = [...currentIds];
    reordered.splice(sourceIdx, 1);
    reordered.splice(targetIdx, 0, sourceId);

    // Optimistic state update
    const reorderedSlates = reordered
      .map((id) => slates.find((s) => s.id === id))
      .filter(Boolean);
    setSlates(reorderedSlates);

    const newConfig = { ...config, slate_order: reordered };
    setConfig(newConfig);
    persistConfig(newConfig, "Order saved");
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTargetId(null);
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <main
      aria-label="Vision board"
      className="min-h-screen relative"
      style={{
        backgroundColor: "hsl(40 18% 92%)",
        backgroundImage:
          "radial-gradient(circle at 0% 0%, rgba(0,0,0,0.03), transparent 70%), radial-gradient(circle at 100% 100%, rgba(0,0,0,0.025), transparent 70%)",
      }}
    >
      {/* Subtle grain overlay for paper-wall feel */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='80' height='80' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Page header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                data-testid="board-back-to-roadmap"
                className="gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Roadmap
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-stone-900">Vision Board</h1>
              <p className="text-sm text-stone-600 mt-0.5">
                One slate per app. What to do next, in plain English.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setEditMode((v) => !v);
                setHoveredClusterId(null);
              }}
              data-testid="board-edit-mode-toggle"
              className="gap-1.5"
            >
              {editMode ? (
                <>
                  <Check className="w-4 h-4" />
                  Done editing
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  Edit slates
                </>
              )}
            </Button>
          </div>
        </header>

        {/* Edit mode hint */}
        {editMode && (
          <Alert
            className="mb-6 bg-stone-50/80 border-stone-200"
            data-testid="board-edit-mode-hint"
          >
            <Wand2 className="w-4 h-4" />
            <AlertTitle className="text-sm">Edit mode is on</AlertTitle>
            <AlertDescription className="text-xs text-stone-600">
              Drag a slate by its handle to reorder. Click any slate&apos;s coloured dot to change its identity colour. Pick a status from each slate&apos;s dropdown to override the automatic value. Your changes save automatically.
            </AlertDescription>
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <div
            data-testid="board-loading-state"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6"
          >
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton
                key={i}
                className="aspect-[1/1.414] rounded-[18px] bg-stone-200/60"
              />
            ))}
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <Alert
            variant="destructive"
            className="mb-6"
            data-testid="board-error-alert"
          >
            <AlertTitle>Couldn&apos;t load the board</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={loadAll}
                data-testid="board-retry-button"
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Slate wall */}
        {!loading && !error && (
          <div
            role="list"
            aria-label="App slates"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6"
          >
            {slates.map((slate) => (
              <SlateCard
                key={slate.id}
                slate={slate}
                editMode={editMode}
                dimmed={isDimmed(slate)}
                isDragging={draggingId === slate.id}
                isDropTarget={editMode && dropTargetId === slate.id && draggingId !== slate.id}
                onColorChange={handleColorChange}
                onStatusChange={handleStatusChange}
                onOpenDetails={setOpenSlate}
                onClusterEnter={onClusterEnter}
                onClusterLeave={onClusterLeave}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        )}

        {/* Legend */}
        {!loading && !error && (
          <div className="mt-10 space-y-2 flex flex-col items-center text-xs text-stone-600">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center">
              <span className="font-medium text-stone-500">Status:</span>
              <Legend swatch="bg-emerald-400" label="Moving — active work" />
              <Legend swatch="bg-amber-400" label="Waiting — needs a decision or input" />
              <Legend swatch="bg-red-400" label="Stuck — blocked, needs attention" />
              <Legend swatch="bg-stone-400" label="Parked — deferred or quiet" />
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center">
              <span className="font-medium text-stone-500">Cluster:</span>
              <Legend
                swatch="bg-red-600"
                label="Emergency comms — EC, CC, Test Call, SIP Server (hover any to see the cluster)"
              />
            </div>
          </div>
        )}
      </div>

      {/* Drill-down sheet */}
      <Sheet open={!!openSlate} onOpenChange={(o) => !o && setOpenSlate(null)}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[480px] overflow-y-auto"
          data-testid={openSlate ? `slate-drill-${openSlate.id}` : undefined}
        >
          {openSlate && (
            <>
              <SheetHeader className="text-left">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: openSlate.identity_color }}
                  />
                  <Badge
                    variant="outline"
                    className="text-[10px]"
                  >
                    {(openSlate.status_tier || "parked").toUpperCase()}
                  </Badge>
                  {isStatusOverridden(openSlate.id) && (
                    <Badge variant="secondary" className="text-[10px]">
                      Manual override
                    </Badge>
                  )}
                  {openSlate.cluster_label && (
                    <Badge
                      variant="outline"
                      className="text-[10px] gap-1"
                      style={{
                        color: openSlate.cluster_color || "#475569",
                        borderColor: openSlate.cluster_color
                          ? `${openSlate.cluster_color}33`
                          : "#cbd5e1",
                        backgroundColor: openSlate.cluster_color
                          ? `${openSlate.cluster_color}10`
                          : "#f1f5f9",
                      }}
                      data-testid={`slate-drill-cluster-${openSlate.id}`}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: openSlate.cluster_color || "#64748b" }}
                      />
                      {openSlate.cluster_label}
                    </Badge>
                  )}
                </div>
                <SheetTitle className="text-xl">{openSlate.title}</SheetTitle>
                <SheetDescription>{openSlate.subtitle}</SheetDescription>
              </SheetHeader>

              <section className="mt-6 space-y-5">
                {/* Stuck reason — surfaced prominently when present */}
                {openSlate.status_tier === "stuck" && openSlate.stuck_reason && (
                  <Alert
                    variant="destructive"
                    className="bg-red-50 border-red-200 text-red-900"
                    data-testid={`slate-drill-stuck-${openSlate.id}`}
                  >
                    <AlertOctagon className="w-4 h-4" />
                    <AlertTitle className="text-sm">Stuck because</AlertTitle>
                    <AlertDescription className="text-sm">
                      {openSlate.stuck_reason}
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <h4 className="text-xs uppercase tracking-wider text-stone-500 font-medium mb-2">
                    What to do next
                  </h4>
                  {openSlate.what_to_do_next && openSlate.what_to_do_next.length > 0 ? (
                    <ol
                      className="space-y-1.5 text-sm text-stone-800 list-none"
                      data-testid={`slate-drill-wtdn-${openSlate.id}`}
                    >
                      {openSlate.what_to_do_next.map((item, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-stone-400 font-semibold mt-0.5">
                            {i + 1}.
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-stone-500 italic">No active items yet.</p>
                  )}
                </div>

                {openSlate.details_paragraphs && openSlate.details_paragraphs.length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-stone-500 font-medium mb-2">
                      Background
                    </h4>
                    <div className="space-y-2 text-sm text-stone-700 leading-relaxed">
                      {openSlate.details_paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </div>
                )}

                {openSlate.deep_links && openSlate.deep_links.length > 0 && (
                  <div className="pt-4 border-t border-stone-200">
                    <h4 className="text-xs uppercase tracking-wider text-stone-500 font-medium mb-2">
                      More detail
                    </h4>
                    <div className="space-y-1.5">
                      {openSlate.deep_links.map((dl, i) => (
                        <Link
                          key={i}
                          to={dl.href}
                          className="flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-900 hover:underline"
                          data-testid={`slate-deep-link-${openSlate.id}-${i}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {dl.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {isStatusOverridden(openSlate.id) && (
                  <div className="pt-4 border-t border-stone-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusReset(openSlate.id)}
                      data-testid={`slate-status-reset-${openSlate.id}`}
                    >
                      Reset status to automatic
                    </Button>
                  </div>
                )}
              </section>
            </>
          )}
        </SheetContent>
      </Sheet>
    </main>
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${swatch}`} aria-hidden="true" />
      {label}
    </span>
  );
}
